import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const FavoriteCharacterButton = ({ characterId, onStatusChange }) => {
  const [isFavorite, setIsFavorite] = useState(false);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [showNotes, setShowNotes] = useState(false);
  const { isLoggedIn } = useAuth();

  // Base API URL - correct the endpoint to use plural "characters"
  const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const API_BASE_URL = `${BASE_URL}/api/user/characters`;

  // Check if the character is already in the user's favorites
  useEffect(() => {
    const checkCharacterStatus = async () => {
      if (!isLoggedIn) {
        setLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setLoading(false);
          return;
        }

        console.log(`Checking favorite status for characterId: ${characterId}`);
        
        // Correct the URL to use the plural form "characters"
        const response = await axios.get(`${API_BASE_URL}/${characterId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success && response.data.data) {
          setIsFavorite(true);
          setNotes(response.data.data.notes || '');
        }
      } catch (error) {
        // 404 is expected if character is not in user's list
        if (error.response && error.response.status !== 404) {
          console.error('Error checking character status:', error);
        }
      } finally {
        setLoading(false);
      }
    };

    checkCharacterStatus();
  }, [characterId, isLoggedIn]);

  const handleFavoriteToggle = async () => {
    if (!isLoggedIn) {
      alert('Please log in to favorite characters');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      if (isFavorite) {
        // Remove from favorites - correct URL to use plural "characters"
        await axios.delete(`${API_BASE_URL}/${characterId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFavorite(false);
        setNotes('');
        if (onStatusChange) onStatusChange(false);
      } else {
        // Add to favorites - correct URL to use plural "characters"
        await axios.post(API_BASE_URL, {
          characterId,
          isFavorite: true,
          notes
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFavorite(true);
        if (onStatusChange) onStatusChange(true);
      }
    } catch (error) {
      console.error('Error updating favorite status:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateNotes = async () => {
    if (!isLoggedIn || !isFavorite) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Correct URL to use plural "characters"
      await axios.patch(`${API_BASE_URL}/${characterId}`, {
        notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setShowNotes(false);
    } catch (error) {
      console.error('Error updating notes:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <button className="favorite-button loading" disabled>
        Loading...
      </button>
    );
  }

  return (
    <div className="favorite-character-container">
      <button
        className={`favorite-button ${isFavorite ? 'favorited' : ''}`}
        onClick={handleFavoriteToggle}
        disabled={loading}
      >
        {isFavorite ? '❤️ Favorited' : '🤍 Add to Favorites'}
      </button>

      {isFavorite && (
        <div className="notes-section">
          <button 
            className="notes-toggle-button"
            onClick={() => setShowNotes(!showNotes)}
          >
            {showNotes ? 'Hide Notes' : 'Show/Edit Notes'}
          </button>
          
          {showNotes && (
            <div className="notes-editor">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add your notes about this character..."
                rows={4}
              />
              <button 
                className="save-notes-button"
                onClick={updateNotes}
                disabled={loading}
              >
                Save Notes
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FavoriteCharacterButton;